import { CheckIcon, ChevronDownIcon, PlusIcon, Trash2Icon, VariableIcon } from "lucide-react";
import { useState } from "react";

import useAddEvaluateInputSets from "../useAddEvaluateInputSets";
import { useEvaluateScope } from "../useEvaluateScope";

import type { VariableSet } from "@/components/Playground/types";
import { getVariableValueIsFilled } from "@/components/Playground/utils";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { PlaygroundEvaluateSheetReference } from "@/queries/playground/evaluate";

interface InputSetsControlProps {
  variableSets: VariableSet[];
  variableNames: string[];
  mappedVariableKeys: string[];
  reference: PlaygroundEvaluateSheetReference;
  sourceRowCount: number;
  disabled: boolean;
  onConfigureInputSet: (setId?: string) => void;
}

const InputSetsControl = ({
  variableSets,
  variableNames,
  mappedVariableKeys,
  reference,
  sourceRowCount,
  disabled,
  onConfigureInputSet,
}: InputSetsControlProps) => {
  const { playground } = useEvaluateScope();
  const { addInputSets, isAddingInputSets, maxInputSets } = useAddEvaluateInputSets({
    mappedVariableKeys,
    reference,
    sourceRowCount,
    variableSetCount: variableSets.length,
  });
  const [open, setOpen] = useState(false);
  const activeSetName = playground.activeInputVariableSet?.name || "Untitled set";

  const editInputSet = (setId?: string) => {
    if (disabled) {
      return;
    }
    setOpen(false);
    onConfigureInputSet(setId);
  };

  let addInputSetDisabledReason: string | undefined;
  if (disabled) {
    addInputSetDisabledReason = "Input sets can’t be changed while this evaluation is locked.";
  } else if (isAddingInputSets) {
    addInputSetDisabledReason = "An input set is being added.";
  } else if (variableSets.length >= maxInputSets) {
    addInputSetDisabledReason = `You can keep up to ${maxInputSets} input sets.`;
  }

  const addInputSet = async () => {
    if (disabled || isAddingInputSets || variableSets.length >= maxInputSets) {
      return;
    }

    const [setId] = await addInputSets(1);
    if (setId) {
      editInputSet(setId);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-8 max-w-full items-center justify-start gap-1.5 rounded-full border border-gray-200/70 bg-gray-50/80 px-2.5 text-sm font-medium leading-none text-gray-600 shadow-none transition-colors hover:border-gray-300 hover:bg-gray-100/80 hover:text-gray-900 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300 data-[state=open]:border-gray-300 data-[state=open]:bg-gray-100 data-[state=open]:text-gray-900"
          aria-label={`${variableSets.length} input ${
            variableSets.length === 1 ? "set" : "sets"
          }. Editor scope: ${activeSetName}.`}
          title={`Manage input sets · Editor scope: ${activeSetName}`}
        >
          <VariableIcon className="h-4 w-4 shrink-0 text-gray-400" />
          <span className="leading-5 text-gray-700">Input sets</span>
          <span className="text-xs font-medium tabular-nums text-gray-400">
            {variableSets.length}
          </span>
          <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        avoidCollisions
        collisionPadding={12}
        sticky="always"
        className="mx-0 overflow-y-auto overscroll-contain rounded-xl border-gray-200 p-0 shadow-lg"
        style={{
          width: "min(320px, calc(100vw - 24px), var(--radix-popover-content-available-width))",
          maxHeight:
            "min(80dvh, calc(100dvh - 24px), var(--radix-popover-content-available-height))",
        }}
        onWheel={event => event.stopPropagation()}
      >
        <div className="border-b border-gray-100 px-3 py-2.5">
          <div className="text-sm font-medium text-gray-800">Input sets</div>
          <p className="mt-0.5 break-words text-xs leading-5 text-gray-500">
            Every input set runs once. Sheet rows provide defaults when available, and the set
            marked Editor scope supplies values shown in the prompt editor.
          </p>
        </div>
        <div className="max-h-64 overflow-y-auto overscroll-contain p-1.5">
          {variableSets.map((variableSet, index) => {
            const overrideCount = variableNames.filter(variableName =>
              getVariableValueIsFilled(
                variableSet.variables.find(variable => variable.key === variableName)?.value,
              ),
            ).length;
            const isEditorScope = playground.activeInputVariableSetId === variableSet.id;
            return (
              <div
                key={variableSet.id}
                className={cn(
                  "group flex items-center gap-1 rounded-md transition-colors hover:bg-gray-50",
                  isEditorScope && "bg-gray-50",
                )}
              >
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-2 px-2 py-2 text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-gray-300 disabled:cursor-default"
                  disabled={disabled}
                  aria-current={isEditorScope ? "true" : undefined}
                  onClick={() => editInputSet(variableSet.id)}
                >
                  <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded bg-gray-100 px-1 text-[10px] font-medium tabular-nums text-gray-500">
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                      <span className="truncate">{variableSet.name || "Untitled set"}</span>
                      {isEditorScope && (
                        <CheckIcon className="h-3.5 w-3.5 shrink-0 text-gray-500" />
                      )}
                    </span>
                    <span className="block text-[11px] text-gray-400">
                      {overrideCount} {overrideCount === 1 ? "override" : "overrides"}
                      {isEditorScope ? " · Editor scope" : !disabled && " · Edit"}
                    </span>
                  </span>
                </button>
                {playground.canRemoveInputVariableSet && !disabled && (
                  <button
                    type="button"
                    className="mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded text-gray-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 focus:opacity-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-red-200 group-hover:opacity-100"
                    onClick={() => playground.removeInputVariableSet(variableSet.id)}
                    aria-label={`Delete ${variableSet.name || "input set"}`}
                    title="Delete input set"
                  >
                    <Trash2Icon className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between border-t border-gray-100 px-2 py-2">
          <span className="px-1 text-[11px] text-gray-400">
            {maxInputSets === 0
              ? "No sheet rows available"
              : `${variableSets.length} of ${maxInputSets} input sets`}
          </span>
          <BasicTooltip
            content={addInputSetDisabledReason}
            shouldShow={Boolean(addInputSetDisabledReason)}
            side="top"
          >
            <span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 gap-1.5 px-2 text-xs text-gray-600"
                disabled={Boolean(addInputSetDisabledReason)}
                onClick={addInputSet}
              >
                <PlusIcon className="h-3.5 w-3.5" />
                {isAddingInputSets ? "Adding…" : "Add Input Set"}
              </Button>
            </span>
          </BasicTooltip>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default InputSetsControl;
